// Глобальный store игры — Zustand.
// Единый источник правды для:
//   - профиля игрока (ник, лига, уровень, статы)
//   - валют (Скрипты, USDT, TON-кошелёк)
//   - прогресса (Боевой Пропуск, ежедневный вход, реферальные)
//   - коллекции (Герои в собственности, колоды)
//
// Раньше каждый экран держал свой MOCK-объект; при изменении одних
// данных другие экраны не подхватывали. Теперь меняем через actions,
// все подписчики автоматически обновляются.
//
// При подключении сервера (Этап 42) initFromServer() будет тянуть
// реальные данные из API; пока заполняем mock + Telegram user.

import { create } from 'zustand';
import { getLeagueDef } from '../i18n/leagues';

export type Faction = 'deus' | 'syntetics' | 'global' | 'livlife' | 'neutral';

export interface Player {
  nick: string;
  role: string;            // 'Фиксер', 'Корпорат', и т.д. (текущая лига)
  level: number;
  heroId: string;          // ID Героя который сейчас "лицо" профиля
  faction: Faction;
  cupProgress: { current: number; max: number };
  firewalls: number;
  unreadMessages: number;
  regDate: string;         // '14.04.26'
  referralCode: string;    // 6-символьный код игрока, см. server/lib/referral-code.ts
  deletionScheduledAt: string | null;  // ISO-дата запланированного удаления (через 7 дней)
}

export interface Currencies {
  scripts: number;
  usdt: number;
  ton: number;
  tonConnected: boolean;
}

export interface Progress {
  hasBattlePass: boolean;
  bpLevel: number;
  bpXp: number;
  dailyDay: number;
  dailyDotsDone: number;
  ratingPlace: string;     // '#1 247'
  league: string;          // 'Фиксеры'
  leagueWinStreak: number;
  leagueWinStreakNeeded: number;
}

export interface Stats {
  season:   { matches: number; winrate: number; streak: number; faction: Faction; factionName: string; hero: string; league: string };
  lifetime: { matches: number; winrate: number; streak: number; faction: Faction; factionName: string; hero: string; league: string };
}

export interface Counts {
  achievements: { current: number; total: number };
  friends: { count: number; online: number };
  referrals: { count: number; scripts: number; ton: number };
  inventory: { items: number };
  quests: number;          // непрочитанные / непринятые
}

// Сводка по колоде игрока (из server profile).
export interface DeckSummary {
  id: number;
  name: string;
  heroId: string;
  faction: Faction;
  cardCount: number;
  isFavorite: boolean;
  updatedAt: string;
  // Полный состав колоды — нужно для передачи в engine при createMatchForBattle.
  // Структура с сервера: [{ cardId: 'deus_xxx', count: 2 }, ...]
  cards: { cardId: string; count: number }[];
}

// БП.1: данные Боевого Пропуска.
export interface BPSeasonState {
  id: number;
  code: string;
  name: string;
  starts_at: string;
  ends_at: string;
}
export interface BPProgressState {
  xp: number;
  level: number;
  xp_in_level: number;
  xp_to_next: number;
  has_paid: boolean;
  paid_purchased_at: string | null;
}
export interface BPCatalogEntry {
  level: number;
  track: 'free' | 'paid';
  reward_type: 'scripts' | 'container' | 'cosmetic' | 'title' | 'perk_unlock';
  payload: any;
  order_in_level: number;
}
export interface BattlePassState {
  season: BPSeasonState | null;
  progress: BPProgressState | null;
  catalog: BPCatalogEntry[];
  claimed: { level: number; track: 'free' | 'paid' }[];
  active_title: { id: number; title_code: string; title_text_ru: string } | null;
  unlocked_perks: string[];
  lore_group_invite_url: string | null;
}

export interface PlayerTitleState {
  id: number;
  title_code: string;
  title_text_ru: string;
  source: string;
}
export interface PlayerCosmeticState {
  id: number;
  cosmetic_id: string;
  cosmetic_type: string;
  is_equipped: boolean;
  source: string;
}

interface GameState {
  player: Player;
  currencies: Currencies;
  progress: Progress;
  stats: Stats;
  counts: Counts;
  // Какие Герои есть у игрока (купленные/полученные)
  heroesOwned: Set<string>;
  // Колоды игрока — синхронизируются с syncFromServer.
  decks: DeckSummary[];
  // Загружен ли state с сервера (false → используем MOCK дефолты)
  serverSynced: boolean;

  // БП.1
  battlePass: BattlePassState | null;
  titles: PlayerTitleState[];
  cosmetics: PlayerCosmeticState[];
  profileAccentColor: string | null;
  activeTitle: { id: number; title_code: string; title_text_ru: string } | null;
  deckSlotsLimit: number;
  // АВ.1
  activeAvatarId: string | null;
  // УР.1: уровень аккаунта (level лежит в player.level — для совместимости
  // с existing UI; здесь — только сопутствующие XP-метрики и next reward).
  accountXp: number;
  accountXpInLevel: number;
  accountXpToNext: number;
  nextLevelReward: { level: number; description: string } | null;
  // ЕЖВ.1
  dailyLogin: import('../api/client').DailyLoginStatus | null;
  // ФТУЕ.1
  ftueStatus: 'pending' | 'in_progress' | 'skipped' | 'completed';
  ftueStep: number;
  // ФТУЕ.6: коды уже пройденных tooltip-флоу (например 'shop', 'bp', ...).
  ftueTooltipsSeen: Set<string>;
  // ЗВЁЗДЫ.2 (Этап 76): очередь непоказанных изъятий карт за Stars-рефанд.
  pendingClawback: Array<{
    stars: number; at: string;
    cards: Array<{ id: string; name_ru: string; rarity: string; faction: string }>;
  }>;

  // === actions ===
  setPlayerName: (name: string) => void;
  addScripts: (amount: number) => void;
  addUsdt: (amount: number) => void;
  setTonConnected: (connected: boolean) => void;
  buyHero: (heroId: string) => void;
  activateBattlePass: () => void;
  claimDaily: () => void;
  // Покупки (Этап 46.1): возвращают true если хватило денег.
  buyForUsdt: (price: number, onSuccess?: () => void) => boolean;
  buyForScripts: (price: number, onSuccess?: () => void) => boolean;
  addFirewall: (n?: number) => void;
  addInventoryItems: (n?: number) => void;
  dismissClawback: () => void;
  // Подтянуть полный профиль с сервера (после auth). Перетирает mock-данные.
  syncFromServer: (data: import('../api/client').ProfileResponse) => void;
  // БП.1
  setBattlePass: (bp: BattlePassState | null) => void;
  setProfileAccentColor: (color: string | null) => void;
  setActiveTitleLocal: (title: { id: number; title_code: string; title_text_ru: string } | null) => void;
  // АВ.1
  setActiveAvatarLocal: (avatarId: string | null) => void;
  // ЕЖВ.1
  setDailyLogin: (s: import('../api/client').DailyLoginStatus | null) => void;
  // ФТУЕ.1
  setFtueLocal: (status: 'pending' | 'in_progress' | 'skipped' | 'completed', step?: number) => void;
  // ФТУЕ.6: пометить tooltip-флоу как пройденный (без API-вызова — это
  // делает хук useTooltipFlow сам, store просто обновляется).
  markFtueTooltipSeenLocal: (code: string) => void;
}

// Стартовые значения для оффлайн-режима. При успешном auth+syncFromServer
// перетираются реальными данными. На клиенте — нулевой состав, чтобы
// явно увидеть когда сервер не отвечает.
const DEFAULTS = {
  player: {
    nick: 'Ник_игрока',
    role: 'Трущобы',
    level: 1,
    heroId: 'neutral_lider_ulicy',
    faction: 'neutral' as Faction,
    cupProgress: { current: 0, max: 5 },
    firewalls: 0,
    unreadMessages: 0,
    regDate: '',
    referralCode: '',
    deletionScheduledAt: null
  },
  currencies: {
    scripts: 0,
    usdt: 0,
    ton: 0,
    tonConnected: false
  },
  progress: {
    hasBattlePass: false,
    bpLevel: 1,
    bpXp: 0,
    dailyDay: 1,
    dailyDotsDone: 0,
    ratingPlace: '—',
    league: 'Трущобы',
    leagueWinStreak: 0,
    leagueWinStreakNeeded: 3
  },
  stats: {
    season:   { matches: 0, winrate: 0, streak: 0, faction: 'neutral' as Faction, factionName: 'УЛИЦА', hero: '—', league: 'Трущобы' },
    lifetime: { matches: 0, winrate: 0, streak: 0, faction: 'neutral' as Faction, factionName: 'УЛИЦА', hero: '—', league: 'Трущобы' }
  },
  counts: {
    achievements: { current: 0, total: 47 },
    friends:      { count: 0, online: 0 },
    referrals:    { count: 0, scripts: 0, ton: 0 },
    inventory:    { items: 0 },
    quests: 0
  },
  heroesOwned: new Set([
    'neutral_lider_ulicy'             // стартовый, у всех
  ]),
  decks: [] as DeckSummary[]
};

export const useGameStore = create<GameState>((set) => ({
  ...DEFAULTS,
  serverSynced: false,
  battlePass: null,
  titles: [],
  cosmetics: [],
  profileAccentColor: null,
  activeTitle: null,
  deckSlotsLimit: 5,
  activeAvatarId: null,
  accountXp: 0,
  accountXpInLevel: 0,
  accountXpToNext: 100,
  nextLevelReward: null,
  dailyLogin: null,
  // ФТУЕ.1 (до приезда профиля считаем что игрок «pending» — гейт это решит).
  ftueStatus: 'pending' as const,
  ftueStep: 0,
  // ФТУЕ.6 (до приезда профиля — пустой Set, ничего не пройдено).
  ftueTooltipsSeen: new Set<string>(),
  pendingClawback: [],

  syncFromServer: (data) => set(() => {
    // ЛИГ.1.x: порог побед подряд зависит от лиги (3/5/7).
    // Для Высшей winsToPromote=null → 0 (UI «прогресса нет, ты на вершине»).
    const leagueDef = getLeagueDef(data.player.league);
    const winsNeededForUi = leagueDef.winsToPromote ?? 0;
    return ({
    serverSynced: true,
    player: {
      nick: data.player.nick,
      role: data.player.league,
      level: data.player.accountLevel ?? 1,
      heroId: data.player.heroId,
      faction: data.player.faction,
      cupProgress: { current: data.player.winStreak, max: winsNeededForUi || 1 },
      firewalls: data.player.firewalls,
      unreadMessages: data.player.unreadMessages,
      regDate: new Date(data.player.regDate).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' }),
      referralCode: data.player.referralCode || '',
      deletionScheduledAt: data.player.deletionScheduledAt || null
    },
    currencies: {
      scripts: data.player.scripts,
      usdt: data.player.usdt,
      ton: 0,
      tonConnected: !!data.player.tonAddress
    },
    progress: {
      hasBattlePass: data.player.hasBp,
      bpLevel: data.player.bpLevel,
      bpXp: data.player.bpXp,
      dailyDay: 1,                       // TODO: добавить в БД
      dailyDotsDone: 0,
      // Этап 70: реальное место по MMR с сервера ('#1 247'-формат).
      ratingPlace: data.player.ratingPlace
        ? `#${Number(data.player.ratingPlace).toLocaleString('ru-RU')}`
        : '—',
      league: data.player.league,
      leagueWinStreak: data.player.winStreak,
      leagueWinStreakNeeded: winsNeededForUi || 0
    },
    heroesOwned: new Set(data.heroesOwned),
    decks: (data.decks || []).map((d: any) => ({
      id: d.id,
      name: d.name,
      heroId: d.heroId,
      faction: d.faction,
      cardCount: Array.isArray(d.cards) ? d.cards.reduce((a: number, c: any) => a + (c.count || 0), 0) : 0,
      isFavorite: !!d.isFavorite,
      updatedAt: d.updatedAt,
      cards: Array.isArray(d.cards) ? d.cards : []
    })),
    // БП.1
    titles: data.titles || [],
    cosmetics: data.cosmetics || [],
    profileAccentColor: data.player.profileAccentColor || null,
    activeTitle: data.player.activeTitle || null,
    deckSlotsLimit: data.player.deckSlotsLimit ?? 5,
    // АВ.1
    activeAvatarId: data.player.activeAvatarId || null,
    // УР.1
    accountXp: data.player.accountXp ?? 0,
    accountXpInLevel: data.player.accountXpInLevel ?? 0,
    accountXpToNext: data.player.accountXpToNext ?? 100,
    nextLevelReward: data.player.nextReward
      ? { level: data.player.nextReward.level, description: data.player.nextReward.description }
      : null,
    // ФТУЕ.1
    ftueStatus: data.player.ftueStatus || 'pending',
    ftueStep: data.player.ftueStep ?? 0,
    // ФТУЕ.6
    ftueTooltipsSeen: new Set(data.player.ftueTooltipsSeen || []),
    // ЗВЁЗДЫ.2: очередь изъятий карт за Stars-рефанд (показ рулеткой).
    pendingClawback: data.player.pendingStarsClawback || []
    });
  }),

  setBattlePass: (bp) => set(() => ({ battlePass: bp })),
  setProfileAccentColor: (color) => set(() => ({ profileAccentColor: color })),
  setActiveTitleLocal: (title) => set(() => ({ activeTitle: title })),
  setActiveAvatarLocal: (avatarId) => set(() => ({ activeAvatarId: avatarId })),
  setDailyLogin: (s) => set(() => ({ dailyLogin: s })),
  setFtueLocal: (status, step) => set(() => ({
    ftueStatus: status,
    ...(typeof step === 'number' ? { ftueStep: step } : {})
  })),
  markFtueTooltipSeenLocal: (code) => set(state => {
    if (state.ftueTooltipsSeen.has(code)) return state;
    const next = new Set(state.ftueTooltipsSeen);
    next.add(code);
    return { ftueTooltipsSeen: next };
  }),

  setPlayerName: (name) =>
    set(state => ({ player: { ...state.player, nick: name } })),

  addScripts: (amount) =>
    set(state => ({ currencies: { ...state.currencies, scripts: state.currencies.scripts + amount } })),

  addUsdt: (amount) =>
    set(state => ({ currencies: { ...state.currencies, usdt: Number((state.currencies.usdt + amount).toFixed(2)) } })),

  setTonConnected: (connected) =>
    set(state => ({ currencies: { ...state.currencies, tonConnected: connected } })),

  buyHero: (heroId) =>
    set(state => ({ heroesOwned: new Set([...state.heroesOwned, heroId]) })),

  activateBattlePass: () =>
    set(state => ({ progress: { ...state.progress, hasBattlePass: true } })),

  claimDaily: () =>
    set(state => ({
      progress: {
        ...state.progress,
        dailyDay: state.progress.dailyDay + 1,
        dailyDotsDone: Math.min(7, state.progress.dailyDotsDone + 1)
      },
      currencies: { ...state.currencies, scripts: state.currencies.scripts + 10 }
    })),

  // Покупки. Возвращают true при успехе, false если не хватает денег.
  buyForUsdt: (price, onSuccess) => {
    const cur = useGameStore.getState().currencies.usdt;
    if (cur < price) return false;
    set(state => ({
      currencies: { ...state.currencies, usdt: Number((state.currencies.usdt - price).toFixed(2)) }
    }));
    onSuccess?.();
    return true;
  },
  buyForScripts: (price, onSuccess) => {
    const cur = useGameStore.getState().currencies.scripts;
    if (cur < price) return false;
    set(state => ({
      currencies: { ...state.currencies, scripts: state.currencies.scripts - price }
    }));
    onSuccess?.();
    return true;
  },
  addFirewall: (n = 1) =>
    set(state => ({ player: { ...state.player, firewalls: state.player.firewalls + n } })),
  addInventoryItems: (n = 1) =>
    set(state => ({
      counts: { ...state.counts, inventory: { items: state.counts.inventory.items + n } }
    })),
  // ЗВЁЗДЫ.2: убрать показанное изъятие из очереди (после рулетки).
  dismissClawback: () =>
    set(state => ({ pendingClawback: state.pendingClawback.slice(1) }))
}));

// Селекторы-shortcuts для удобства в компонентах.
// const player = useGameStore(s => s.player) — переподпишется только на player.
// const scripts = useGameStore(s => s.currencies.scripts) — только на этот ключ.
export const usePlayer     = () => useGameStore(s => s.player);
export const useCurrencies = () => useGameStore(s => s.currencies);
export const useProgress   = () => useGameStore(s => s.progress);
export const useStats      = () => useGameStore(s => s.stats);
export const useCounts     = () => useGameStore(s => s.counts);
export const useHeroesOwned= () => useGameStore(s => s.heroesOwned);
